/**
 * useAnimatedNavigate — Drop-in replacement for useNavigate that wraps
 * navigations in the View Transitions API for smooth page transitions.
 *
 * Usage:
 *   const navigate = useAnimatedNavigate();
 *   navigate('/billing/invoices/123');
 *
 * Accepts the same overloads as React Router's navigate().
 * Falls back to plain navigation when View Transitions API isn't available.
 *
 * Designed for app-wide adoption — import from here instead of react-router-dom.
 */
import { useCallback } from 'react';
import { useNavigate, type NavigateOptions, type To } from 'react-router-dom';
import { useViewTransition } from './useViewTransition';

export function useAnimatedNavigate() {
  const navigate = useNavigate();
  const { startTransition } = useViewTransition();

  const animatedNavigate = useCallback(
    (to: To | number, options?: NavigateOptions) => {
      startTransition(() => {
        if (typeof to === 'number') {
          navigate(to);
        } else {
          navigate(to, options);
        }
      });
    },
    [navigate, startTransition],
  );

  return animatedNavigate;
}
